<script>
    import { createEventDispatcher } from 'svelte';
    import Icon from '../icons/Icon.svelte';

    export let show = false;
    export let selectedTable = null;
    export let products = [];
    export let categories = [];
    export let pendingOrderItems = [];
    export let pendingSubtotal = 0;
    export let printDestination = 'kitchen';
    export let isSubmittingBatch = false;
    export let quickAddingProductIds = new Set();
    export let closeIncludeService = true;
    export let closeDiscount = 0;
    export let splitTotalPaid = 0;
    export let splitRemaining = 0;
    export let splitProgressPercent = 0;

    let showProductPicker = false;
    let productSearch = '';

    const dispatch = createEventDispatcher();

    function formatCurrency(val) {
        const num = parseFloat(val);
        return isNaN(num) ? '0,00' : num.toFixed(2).replace('.', ',');
    }

    function getTimeDiff(dateStr) {
        if (!dateStr) return '0 min';
        const start = new Date(dateStr).getTime();
        const now = Date.now();
        const diffMinutes = Math.max(0, Math.floor((now - start) / (1000 * 60)));
        if (diffMinutes < 60) return `${diffMinutes} min`;
        const hours = Math.floor(diffMinutes / 60);
        const mins = diffMinutes % 60;
        return `${hours}h ${mins}m`;
    }

    function getDisplayNotes(notes, modifiers) {
        if (!notes || typeof notes !== 'string') return '';
        let clean = notes.trim();
        if (!clean) return '';
        const obsIndex = clean.indexOf('// Obs:');
        if (obsIndex !== -1) {
            return clean.substring(obsIndex + 7).trim();
        }
        if (modifiers && Array.isArray(modifiers) && modifiers.length > 0) {
            if (clean.startsWith('+ ') || clean.startsWith('• ')) {
                const modNames = modifiers.map(m => (typeof m === 'object' && m ? m.name : String(m)).trim().toLowerCase());
                const parts = clean.split(/\s*\|\s*/).map(p => p.replace(/^[+•]\s*/, '').replace(/\s*\(R\$.*?\)/, '').trim().toLowerCase());
                const allMatch = parts.every(p => modNames.includes(p));
                if (allMatch) {
                    return '';
                }
            }
        }
        return clean;
    }

    function isSimpleProduct(prod) {
        if (!prod) return false;
        const groups = prod.modifierGroups || prod.modifier_groups;
        if (!Array.isArray(groups)) return false;
        if (groups.length > 0) return false;
        const catList = Array.isArray(categories) ? categories : [];
        const isPizza = (prod.category_name && prod.category_name.toLowerCase().includes('pizza')) ||
            (prod.name && prod.name.toLowerCase().includes('pizza')) ||
            (catList.find(c => c.id === prod.category_id || c.id === prod.categoryId)?.name?.toLowerCase().includes('pizza'));
        if (isPizza) return false;
        return true;
    }

    $: filteredProducts = products.filter(p => {
        if (!productSearch.trim()) return true;
        const q = productSearch.toLowerCase().trim();
        const nameMatch = (p.name || '').toLowerCase().includes(q);
        const descMatch = (p.description || '').toLowerCase().includes(q);
        const catMatch = (p.category_name || '').toLowerCase().includes(q);
        return nameMatch || descMatch || catMatch;
    });

    function handleClose() {
        dispatch('close');
    }
</script>

{#if show && selectedTable && selectedTable.comanda}
    <div class="modal-overlay" on:click|self={handleClose}>
        <div class="modal-card comanda-modal">
            <div class="modal-header comanda-header">
                <div class="comanda-title-group">
                    <span class="comanda-number">#{String(selectedTable.table_number).padStart(2, '0')}</span>
                    <div>
                        <div class="comanda-title-line">
                            <h3>{selectedTable.label}</h3>
                            <span class="comanda-client-pill">
                                <Icon name="user" size={12} />
                                {selectedTable.comanda.customer_name || 'Cliente Salão'}
                            </span>
                            <span class="status-pill status-{selectedTable.status}">
                                {#if selectedTable.status === 'occupied'}
                                    <span class="pill-dot blue"></span> Ocupada
                                {:else if selectedTable.status === 'bill_requested'}
                                    <span class="pill-dot orange"></span> Conta Solicitada
                                {:else}
                                    Livre
                                {/if}
                            </span>
                        </div>
                        <span class="comanda-subtitle">
                            <Icon name="clock" size={12} /> Aberta há {getTimeDiff(selectedTable.opened_at || selectedTable.comanda.opened_at)} • <Icon name="users" size={12} /> {selectedTable.comanda.people_count || 1} pessoas
                        </span>
                    </div>
                </div>
                <button class="close-btn" on:click={handleClose} type="button">
                    <Icon name="x" size={16} />
                </button>
            </div>

            <div class="comanda-content">
                <!-- Coluna Esquerda: Itens Lançados -->
                <div class="comanda-items-col">
                    <div class="items-header">
                        <h4>Itens na Comanda ({(selectedTable.comanda.items || []).length})</h4>
                        <button class="btn-add-item" on:click={() => showProductPicker = !showProductPicker} type="button">
                            <Icon name="plus" size={14} />
                            <span>{showProductPicker ? 'Fechar Catálogo' : 'Adicionar Produto'}</span>
                        </button>
                    </div>

                    <!-- Seletor Rápido de Produtos do Cardápio -->
                    {#if showProductPicker}
                        <div class="quick-product-picker">
                            <input 
                                type="text" 
                                class="picker-search" 
                                placeholder="Buscar produto no cardápio..." 
                                bind:value={productSearch}
                            />
                            <div class="picker-list">
                                {#if filteredProducts.length === 0}
                                    <div class="picker-empty-notice">
                                        <p>Nenhum produto encontrado no cardápio.</p>
                                        <span>Tente outro termo ou cadastre na aba Cardápio.</span>
                                    </div>
                                {:else}
                                    {#each filteredProducts as prod}
                                        {@const simple = isSimpleProduct(prod)}
                                        {@const isAdding = quickAddingProductIds && typeof quickAddingProductIds.has === 'function' ? quickAddingProductIds.has(prod.id) : false}
                                        <div
                                            class="picker-item"
                                            class:is-simple={simple}
                                            class:is-adding={isAdding}
                                            on:click={() => {
                                                if (simple) {
                                                    dispatch('quickAddProduct', prod);
                                                } else {
                                                    dispatch('configureProduct', prod);
                                                }
                                            }}
                                        >
                                            <div class="picker-item-info">
                                                <div class="picker-item-title-row">
                                                    <strong>{prod.name}</strong>
                                                    {#if simple}
                                                        <span class="badge-quick-add" title="Adiciona 1 unidade diretamente à mesa">1 Toque</span>
                                                    {/if}
                                                </div>
                                                <span class="picker-price">R$ {formatCurrency(parseFloat(prod.promo_price) > 0 ? prod.promo_price : prod.price)}</span>
                                            </div>
                                            <div class="picker-item-actions">
                                                {#if simple}
                                                    <button
                                                        type="button"
                                                        class="picker-add-btn quick-btn"
                                                        disabled={isAdding}
                                                        title="Lançar 1x diretamente na comanda"
                                                    >
                                                        {isAdding ? 'Lançando...' : '+ Adicionar'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        class="picker-opt-btn"
                                                        title="Abrir opções de personalização / rascunho"
                                                        on:click|stopPropagation={() => dispatch('configureProduct', prod)}
                                                    >
                                                        Opções
                                                    </button>
                                                {:else}
                                                    <button
                                                        type="button"
                                                        class="picker-add-btn configure-btn"
                                                    >
                                                        Personalizar
                                                    </button>
                                                {/if}
                                            </div>
                                        </div>
                                    {/each}
                                {/if}
                            </div>
                        </div>
                    {/if}

                    <!-- Bloco de Novo Pedido em Lote (Lançamento da Mesa Inteira) -->
                    {#if pendingOrderItems.length > 0}
                        <div class="pending-batch-card">
                            <div class="batch-header">
                                <div class="batch-title-wrap">
                                    <span class="batch-badge"><Icon name="clock" size={12} /> Novo Pedido a Enviar ({pendingOrderItems.length} {pendingOrderItems.length === 1 ? 'item' : 'itens'})</span>
                                    <span class="batch-subtotal">Subtotal: <strong>R$ {formatCurrency(pendingSubtotal)}</strong></span>
                                </div>
                                <button type="button" class="btn-batch-clear" on:click={() => dispatch('clearPendingOrder')} title="Descartar itens pendentes">
                                    Descartar Lote
                                </button>
                            </div>

                            <div class="batch-items-list">
                                {#each pendingOrderItems as pItem, pIdx}
                                    <div class="batch-item-row">
                                        <div class="stepper-mini">
                                            <button 
                                                type="button" 
                                                class="btn-step-mini" 
                                                on:click|stopPropagation={() => dispatch('updatePendingItemQty', { index: pIdx, delta: -1 })}
                                                title="Diminuir"
                                            >−</button>
                                            <span class="step-mini-val">{pItem.qty}x</span>
                                            <button 
                                                type="button" 
                                                class="btn-step-mini" 
                                                on:click|stopPropagation={() => dispatch('updatePendingItemQty', { index: pIdx, delta: 1 })}
                                                title="Aumentar"
                                            >+</button>
                                        </div>
                                        <div class="batch-item-info">
                                            <strong>{pItem.fullName || (pItem.isHalfAndHalf && pItem.flavor1Name && pItem.flavor2Name ? `Pizza Meio a Meio (1/2 ${pItem.flavor1Name} + 1/2 ${pItem.flavor2Name})` : pItem.name)}</strong>
                                            {#if pItem.modifiers && Array.isArray(pItem.modifiers) && pItem.modifiers.length > 0}
                                                <div class="item-modifiers-wrap">
                                                    {#each pItem.modifiers as mod}
                                                        {#if (typeof mod === 'object' && mod && mod.name) || (typeof mod === 'string' && mod.trim())}
                                                            <span class="item-modifier-chip">
                                                                + {typeof mod === 'object' ? mod.name : mod}
                                                                {#if typeof mod === 'object' && Number(mod.price) > 0}
                                                                    <span class="modifier-price">(+R$ {formatCurrency(mod.price)})</span>
                                                                {/if}
                                                            </span>
                                                        {/if}
                                                    {/each}
                                                </div>
                                            {/if}
                                            {#if getDisplayNotes(pItem.notes, pItem.modifiers)}
                                                <small class="batch-item-notes">Obs: {getDisplayNotes(pItem.notes, pItem.modifiers)}</small>
                                            {/if}
                                        </div>
                                        <div class="batch-item-price">
                                            R$ {formatCurrency(pItem.price * pItem.qty)}
                                        </div>
                                        <button type="button" class="btn-item-edit-p" on:click={() => dispatch('editPendingItem', { item: pItem, index: pIdx })} title="Editar item do lote">
                                            <Icon name="edit" size={13} />
                                        </button>
                                        <button type="button" class="btn-item-delete" on:click={() => dispatch('removePendingItem', pIdx)} title="Remover do lote">
                                            <Icon name="x" size={14} />
                                        </button>
                                    </div>
                                {/each}
                            </div>

                            <!-- Destino Térmico e Envio Consolidado -->
                            <div class="batch-footer-controls">
                                <div class="batch-dest-bar">
                                    <span class="batch-dest-label">Destino do Cupom:</span>
                                    <div class="batch-dest-pills">
                                        <button 
                                            type="button" 
                                            class="dest-pill" 
                                            class:active={printDestination === 'kitchen'} 
                                            on:click={() => dispatch('setPrintDestination', 'kitchen')}
                                        >
                                            <Icon name="chef-hat" size={13} /> Cozinha
                                        </button>
                                        <button 
                                            type="button" 
                                            class="dest-pill" 
                                            class:active={printDestination === 'cashier'} 
                                            on:click={() => dispatch('setPrintDestination', 'cashier')}
                                        >
                                            <Icon name="dollar-sign" size={13} /> Caixa
                                        </button>
                                        <button 
                                            type="button" 
                                            class="dest-pill" 
                                            class:active={printDestination === 'both'} 
                                            on:click={() => dispatch('setPrintDestination', 'both')}
                                        >
                                            <Icon name="printer" size={13} /> Ambos
                                        </button>
                                    </div>
                                </div>

                                <div class="batch-actions-bar">
                                    <button 
                                        type="button" 
                                        class="btn-action-outline btn-batch-save-only" 
                                        disabled={isSubmittingBatch}
                                        on:click={() => dispatch('submitBatchOrder', { shouldPrint: false })}
                                    >
                                        Salvar Sem Imprimir
                                    </button>
                                    <button 
                                        type="button" 
                                        class="btn-primary btn-batch-send" 
                                        class:cashier-btn={printDestination === 'cashier'}
                                        disabled={isSubmittingBatch}
                                        on:click={() => dispatch('submitBatchOrder', { shouldPrint: true })}
                                    >
                                        {#if isSubmittingBatch}
                                            Enviando Pedido...
                                        {:else if printDestination === 'kitchen'}
                                            <Icon name="zap" size={14} /> Enviar p/ Cozinha (R$ {formatCurrency(pendingSubtotal)})
                                        {:else if printDestination === 'cashier'}
                                            <Icon name="zap" size={14} /> Enviar p/ Caixa (R$ {formatCurrency(pendingSubtotal)})
                                        {:else}
                                            <Icon name="zap" size={14} /> Enviar p/ Ambos (R$ {formatCurrency(pendingSubtotal)})
                                        {/if}
                                    </button>
                                </div>
                            </div>
                        </div>
                    {/if}

                    <div class="comanda-items-list">
                        {#if !selectedTable.comanda.items || selectedTable.comanda.items.length === 0}
                            <div class="empty-items-notice">
                                <p>Nenhum item lançado nesta mesa ainda.</p>
                                <span>Clique em "+ Adicionar Produto" acima para lançar pedidos da cozinha.</span>
                            </div>
                        {:else}
                            <table class="comanda-table">
                                <thead>
                                    <tr>
                                        <th>Qtd</th>
                                        <th>Item</th>
                                        <th>Preço Un.</th>
                                        <th>Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {#each selectedTable.comanda.items as item, index}
                                        <tr>
                                            <td class="qty-cell">
                                                <div class="stepper-mini">
                                                    <button 
                                                        type="button" 
                                                        class="btn-step-mini" 
                                                        on:click|stopPropagation={() => dispatch('updateItemQty', { index, delta: -1 })}
                                                        title="Diminuir quantidade"
                                                    >−</button>
                                                    <span class="step-mini-val">{item.qty}x</span>
                                                    <button 
                                                        type="button" 
                                                        class="btn-step-mini" 
                                                        on:click|stopPropagation={() => dispatch('updateItemQty', { index, delta: 1 })}
                                                        title="Aumentar quantidade"
                                                    >+</button>
                                                </div>
                                                <span class="unit-price-mobile">R$ {formatCurrency(item.price)} un</span>
                                            </td>
                                            <td class="name-cell">
                                                <div class="name-cell-header">
                                                    <div class="name-title-box">
                                                        <strong>{item.fullName || (item.isHalfAndHalf && item.flavor1Name && item.flavor2Name ? `Pizza Meio a Meio (1/2 ${item.flavor1Name} + 1/2 ${item.flavor2Name})` : item.name)}</strong>
                                                        {#if item.isEdited}
                                                            <span class="badge-edited">
                                                                <Icon name="edit" size={10} />
                                                                <span>Alterado</span>
                                                            </span>
                                                        {/if}
                                                    </div>
                                                    <span class="mobile-item-total">R$ {formatCurrency(item.price * item.qty)}</span>
                                                </div>
                                                {#if item.modifiers && Array.isArray(item.modifiers) && item.modifiers.length > 0}
                                                    <div class="item-modifiers-wrap">
                                                        {#each item.modifiers as mod}
                                                            {#if (typeof mod === 'object' && mod && mod.name) || (typeof mod === 'string' && mod.trim())}
                                                                <span class="item-modifier-chip">
                                                                    + {typeof mod === 'object' ? mod.name : mod}
                                                                    {#if typeof mod === 'object' && Number(mod.price) > 0}
                                                                        <span class="modifier-price">(+R$ {formatCurrency(mod.price)})</span>
                                                                    {/if}
                                                                </span>
                                                            {/if}
                                                        {/each}
                                                    </div>
                                                {/if}
                                                {#if getDisplayNotes(item.notes, item.modifiers)}
                                                    <small class="item-notes">Obs: {getDisplayNotes(item.notes, item.modifiers)}</small>
                                                {/if}
                                            </td>
                                            <td class="price-cell">R$ {formatCurrency(item.price)}</td>
                                            <td class="total-cell">R$ {formatCurrency(item.price * item.qty)}</td>
                                            <td class="actions-cell">
                                                <div class="actions-group">
                                                    {#if (item.isEdited && item.batchId) || (item.productionChanges && item.productionChanges.length > 0)}
                                                        <button 
                                                            type="button" 
                                                            class="btn-item-rectify" 
                                                            on:click|stopPropagation={() => dispatch('printRectification', item)} 
                                                            title="Imprimir Retificação de Produção para a Cozinha"
                                                        >
                                                            <Icon name="printer" size={11} />
                                                            <span>Retificação</span>
                                                        </button>
                                                    {/if}
                                                    <button 
                                                        type="button" 
                                                        class="btn-item-edit" 
                                                        on:click={() => dispatch('editItem', item)} 
                                                        title="Editar item"
                                                    >
                                                        <Icon name="edit" size={15} />
                                                    </button>
                                                    <button class="btn-item-delete" on:click={() => dispatch('removeItem', index)} title="Estornar item">
                                                        <Icon name="trash" size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    {/each}
                                </tbody>
                            </table>
                        {/if}
                    </div>
                </div>

                <!-- Coluna Direita: Resumo Financeiro & Ações -->
                <div class="comanda-summary-col">
                    <div class="summary-card">
                        <h4>Resumo da Conta</h4>
                        <div class="summary-row">
                            <span>Subtotal Produtos:</span>
                            <strong>R$ {formatCurrency(selectedTable.comanda.subtotal)}</strong>
                        </div>

                        <label class="service-fee-row">
                            <input type="checkbox" bind:checked={closeIncludeService} />
                            <span>Taxa de Serviço 10% (Opcional):</span>
                            <strong>R$ {formatCurrency(closeIncludeService ? selectedTable.comanda.subtotal * 0.10 : 0)}</strong>
                        </label>

                        <div class="summary-divider"></div>

                        <div class="summary-total-row">
                            <span>Total Geral:</span>
                            <span class="total-highlight">
                                R$ {formatCurrency(selectedTable.comanda.subtotal + (closeIncludeService ? selectedTable.comanda.subtotal * 0.10 : 0) - closeDiscount)}
                            </span>
                        </div>

                        {#if (selectedTable.comanda.payments || []).length > 0}
                            <div class="summary-divider"></div>
                            <div class="split-status-box">
                                <div class="split-status-line">
                                    <span>Já Recebido:</span>
                                    <span class="split-paid-val">R$ {formatCurrency(splitTotalPaid)}</span>
                                </div>
                                <div class="split-status-line">
                                    <span>Saldo Restante:</span>
                                    <span class="split-remain-val">R$ {formatCurrency(splitRemaining)}</span>
                                </div>
                                <div class="split-mini-progress">
                                    <div class="split-mini-fill" style="width: {splitProgressPercent}%"></div>
                                </div>
                                <small class="split-mini-caption">{selectedTable.comanda.payments.length} pagamento(s) recebido(s)</small>
                            </div>
                        {/if}
                    </div>

                    <div class="comanda-actions">
                        <button class="btn-action-outline split-action-btn" on:click={() => dispatch('openSplitModal')}>
                            <Icon name="users" size={16} />
                            <span>Dividir Conta / Pagamento Parcial</span>
                        </button>

                        <button class="btn-action-outline print primary-print" on:click={() => dispatch('printPreBill')}>
                            <Icon name="printer" size={16} />
                            <span>Imprimir Pré-Conta (80mm)</span>
                        </button>

                        <div class="print-sub-row">
                            <button class="btn-action-outline print kitchen-btn-action" on:click={() => dispatch('printReceipt', { type: 'kitchen' })}>
                                <Icon name="utensils" size={14} />
                                <span>Cozinha</span>
                            </button>

                            <button class="btn-action-outline print cashier-btn-action" on:click={() => dispatch('printReceipt', { type: 'cashier' })}>
                                <Icon name="dollar-sign" size={14} />
                                <span>Caixa & Balcão</span>
                            </button>
                        </div>

                        <button class="btn-action-outline" on:click={() => dispatch('openTransferModal')}>
                            <Icon name="refresh" size={15} />
                            <span>Transferir Mesa</span>
                        </button>

                        <button class="btn-primary full" on:click={() => dispatch('openCloseModal')}>
                            <Icon name="check-circle" size={16} />
                            <span>Fechar Conta & Receber</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    .modal-overlay {
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(15, 23, 42, 0.6);
        backdrop-filter: blur(2px);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        overflow-y: auto;
        box-sizing: border-box;
        z-index: 9999;
    }

    .modal-card.comanda-modal {
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 12px;
        max-width: 920px;
        width: 95%;
        max-height: calc(100vh - 32px);
        max-height: calc(100dvh - 32px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 20px 24px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        box-sizing: border-box;
    }

    .modal-header.comanda-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        flex-shrink: 0;
    }

    .comanda-title-group {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .comanda-number {
        font-size: 20px;
        font-weight: 900;
        color: #FF6A00;
        background: #FFF4EC;
        padding: 6px 12px;
        border-radius: 8px;
    }

    .comanda-title-line {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .comanda-title-line h3 {
        margin: 0;
        font-size: 17px;
        font-weight: 800;
        color: #0F172A;
    }

    .comanda-client-pill {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #475569;
        background: #F1F5F9;
        padding: 2px 8px;
        border-radius: 6px;
    }

    .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11px;
        font-weight: 700;
        padding: 2px 8px;
        border-radius: 6px;
    }

    .status-pill.status-occupied {
        background: #EFF6FF;
        color: #2563EB;
    }

    .status-pill.status-bill_requested {
        background: #FFF7ED;
        color: #EA580C;
    }

    .pill-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
    }

    .pill-dot.blue { background: #2563EB; }
    .pill-dot.orange { background: #EA580C; }

    .comanda-subtitle {
        font-size: 12px;
        color: #64748B;
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 3px;
    }

    .close-btn {
        background: transparent;
        border: none;
        color: #64748B;
        cursor: pointer;
        padding: 4px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s, color 0.15s;
    }

    .close-btn:hover {
        background: #F1F5F9;
        color: #0F172A;
    }

    /* CONTENT LAYOUT */
    .comanda-content {
        display: grid;
        grid-template-columns: 1.55fr 1fr;
        gap: 20px;
        flex: 1;
        min-height: 0;
        overflow: hidden;
    }

    .comanda-items-col {
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        padding-right: 4px;
        gap: 12px;
    }

    .comanda-summary-col {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        min-height: 0;
        padding-right: 2px;
    }

    .items-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .items-header h4 {
        margin: 0;
        font-size: 15px;
        font-weight: 700;
        color: #0F172A;
    }

    .btn-add-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #FFF4EC;
        color: #FF6A00;
        border: 1px solid #FED7AA;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: background 0.15s;
    }

    .btn-add-item:hover {
        background: #FFEDD5;
    }

    /* QUICK PRODUCT PICKER */
    .quick-product-picker {
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .picker-search {
        width: 100%;
        padding: 8px 10px;
        border-radius: 6px;
        border: 1px solid #CBD5E1;
        font-size: 12px;
        box-sizing: border-box;
        outline: none;
    }

    .picker-search:focus {
        border-color: #FF6A00;
    }

    .picker-list {
        max-height: 180px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .picker-empty-notice {
        padding: 16px;
        text-align: center;
        color: #94A3B8;
        font-size: 12px;
    }

    .picker-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 10px;
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 6px;
        cursor: pointer;
        transition: border-color 0.15s, background 0.15s;
    }

    .picker-item:hover {
        border-color: #FF6A00;
        background: #FFF7ED;
    }

    .picker-item-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .picker-item-title-row {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
    }

    .badge-quick-add {
        font-size: 10px;
        color: #16A34A;
        background: #DCFCE7;
        padding: 1px 5px;
        border-radius: 4px;
        font-weight: 700;
    }

    .picker-price {
        color: #FF6A00;
        font-weight: 700;
        font-size: 12px;
    }

    .picker-item-actions {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .picker-add-btn {
        background: #FF6A00;
        color: #FFFFFF;
        border: none;
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
    }

    .picker-opt-btn {
        background: #F1F5F9;
        color: #475569;
        border: 1px solid #CBD5E1;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
    }

    /* PENDING BATCH */
    .pending-batch-card {
        background: #FFF7ED;
        border: 1.5px solid #FDBA74;
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .batch-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .batch-title-wrap {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .batch-badge {
        font-size: 11px;
        font-weight: 700;
        color: #C2410C;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .batch-subtotal {
        font-size: 12px;
        color: #9A3412;
    }

    .btn-batch-clear {
        background: transparent;
        border: none;
        color: #DC2626;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
    }

    .batch-items-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .batch-item-row {
        display: flex;
        align-items: center;
        gap: 8px;
        background: #FFFFFF;
        border: 1px solid #FED7AA;
        border-radius: 6px;
        padding: 6px 8px;
    }

    .batch-item-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        font-size: 12px;
    }

    .batch-item-price {
        font-weight: 700;
        font-size: 12px;
        color: #EA580C;
    }

    .btn-item-edit-p {
        background: transparent;
        border: none;
        color: #64748B;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .btn-item-delete {
        background: transparent;
        border: none;
        color: #94A3B8;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .btn-item-delete:hover {
        color: #DC2626;
    }

    .batch-dest-bar {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
    }

    .batch-dest-label {
        font-size: 11px;
        font-weight: 700;
        color: #9A3412;
    }

    .batch-dest-pills {
        display: flex;
        gap: 4px;
    }

    .batch-actions-bar {
        display: flex;
        gap: 8px;
        margin-top: 6px;
    }

    .btn-batch-save-only {
        flex: 1;
        font-size: 12px;
        height: 36px;
    }

    .btn-batch-send {
        flex: 1.4;
        font-size: 12px;
        height: 36px;
    }

    /* STEPPER MINI */
    .stepper-mini {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: #F1F5F9;
        border: 1px solid #E2E8F0;
        border-radius: 6px;
        padding: 2px 4px;
    }

    .btn-step-mini {
        width: 20px;
        height: 20px;
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        border-radius: 4px;
        color: #0F172A;
        font-weight: 800;
        font-size: 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
    }

    .step-mini-val {
        font-size: 12px;
        font-weight: 700;
        color: #0F172A;
        min-width: 22px;
        text-align: center;
    }

    /* COMANDA TABLE */
    .comanda-items-list {
        flex: 1;
        min-height: 140px;
        overflow-y: auto;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        background: #FFFFFF;
    }

    .empty-items-notice {
        padding: 30px;
        text-align: center;
        color: #64748B;
        font-size: 13px;
    }

    .empty-items-notice p {
        margin: 0 0 4px;
        font-weight: 700;
        color: #334155;
    }

    .comanda-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }

    .comanda-table th {
        text-align: left;
        background: #F8FAFC;
        padding: 8px 12px;
        font-size: 11px;
        color: #475569;
        text-transform: uppercase;
        border-bottom: 1px solid #E2E8F0;
        position: sticky;
        top: 0;
        z-index: 2;
    }

    .comanda-table td {
        padding: 10px 12px;
        border-bottom: 1px solid #F1F5F9;
        vertical-align: middle;
    }

    .qty-cell {
        font-weight: 800;
        color: #FF6A00;
        white-space: nowrap;
    }

    .unit-price-mobile {
        display: none;
    }

    .name-cell-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .name-title-box {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
    }

    .name-cell strong {
        color: #0F172A;
        word-break: break-word;
    }

    .badge-edited {
        background: #FEF3C7;
        color: #D97706;
        border: 1px solid #FCD34D;
        font-size: 10px;
        font-weight: 700;
        padding: 1px 5px;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        gap: 3px;
    }

    .mobile-item-total {
        display: none;
    }

    .item-notes, .batch-item-notes {
        display: block;
        color: #64748B;
        font-size: 11px;
        margin-top: 2px;
    }

    .item-modifiers-wrap {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 3px;
    }

    .item-modifier-chip {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        background: #F1F5F9;
        color: #334155;
        border: 1px solid #CBD5E1;
        font-size: 11px;
        font-weight: 600;
        padding: 1px 6px;
        border-radius: 4px;
    }

    .modifier-price {
        color: #16A34A;
        font-weight: 700;
        font-size: 10px;
    }

    .price-cell {
        color: #64748B;
        white-space: nowrap;
    }

    .total-cell {
        font-weight: 700;
        color: #0F172A;
        white-space: nowrap;
    }

    .actions-cell {
        text-align: right;
        white-space: nowrap;
    }

    .actions-group {
        display: flex;
        align-items: center;
        gap: 6px;
        justify-content: flex-end;
    }

    .btn-item-rectify {
        background: #FEF3C7;
        border: 1px solid #F59E0B;
        color: #B45309;
        cursor: pointer;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    .btn-item-edit {
        background: transparent;
        border: none;
        color: #64748B;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .btn-item-edit:hover {
        color: #0F172A;
        background: #F1F5F9;
    }

    /* SUMMARY CARD */
    .summary-card {
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        padding: 14px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 12px;
    }

    .summary-card h4 {
        margin: 0 0 4px;
        font-size: 14px;
        font-weight: 800;
        color: #0F172A;
    }

    .summary-row {
        display: flex;
        justify-content: space-between;
        font-size: 13px;
        color: #475569;
    }

    .service-fee-row {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #475569;
        cursor: pointer;
    }

    .service-fee-row strong {
        margin-left: auto;
    }

    .summary-divider {
        height: 1px;
        background: #E2E8F0;
        margin: 4px 0;
    }

    .summary-total-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;
        font-weight: 700;
        color: #0F172A;
    }

    .total-highlight {
        font-size: 18px;
        font-weight: 900;
        color: #FF6A00;
    }

    .split-status-box {
        display: flex;
        flex-direction: column;
        gap: 4px;
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 6px;
        padding: 8px;
    }

    .split-status-line {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
    }

    .split-paid-val { color: #16A34A; font-weight: 700; }
    .split-remain-val { color: #EA580C; font-weight: 700; }

    .split-mini-progress {
        height: 4px;
        background: #F1F5F9;
        border-radius: 2px;
        overflow: hidden;
        margin: 2px 0;
    }

    .split-mini-fill {
        height: 100%;
        background: #16A34A;
        transition: width 0.3s;
    }

    .split-mini-caption {
        font-size: 10px;
        color: #64748B;
        text-align: right;
    }

    /* ACTIONS */
    .comanda-actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .btn-action-outline {
        height: 38px;
        padding: 0 14px;
        border: 1px solid #E2E8F0;
        background: #FFFFFF;
        color: #334155;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        transition: all 0.15s;
    }

    .btn-action-outline:hover {
        background: #F8FAFC;
        border-color: #CBD5E1;
        color: #0F172A;
    }

    .print-sub-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px;
    }

    .btn-primary {
        height: 42px;
        border: none;
        background: #FF6A00;
        color: #FFFFFF;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 800;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        box-shadow: 0 2px 4px rgba(255, 106, 0, 0.25);
        transition: all 0.15s;
    }

    .btn-primary:hover {
        background: #EA580C;
    }

    .dest-pill {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        border-radius: 4px;
        font-size: 11px;
        color: #475569;
        cursor: pointer;
    }

    .dest-pill.active {
        background: #0F172A;
        border-color: #0F172A;
        color: #FFFFFF;
    }

    @media (max-width: 767.98px) {
        .modal-card.comanda-modal {
            width: 100vw !important;
            max-width: 100vw !important;
            height: 100vh !important;
            height: 100dvh !important;
            max-height: 100dvh !important;
            border-radius: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            position: fixed !important;
            inset: 0 !important;
            z-index: 9999 !important;
            background: #FFFFFF !important;
        }

        .modal-header.comanda-header {
            padding: 12px 14px !important;
            margin-bottom: 0 !important;
            border-bottom: 1px solid #E2E8F0 !important;
            background: #FFFFFF !important;
            flex-shrink: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 10px !important;
        }

        .modal-header.comanda-header .close-btn {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            min-height: 44px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            border-radius: 8px !important;
            background: #F1F5F9 !important;
            color: #0F172A !important;
            touch-action: manipulation;
        }

        .comanda-title-group {
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
        }

        .comanda-number {
            font-size: 16px !important;
            font-weight: 800 !important;
            padding: 4px 10px !important;
            border-radius: 6px !important;
        }

        .comanda-body {
            flex: 1 !important;
            overflow-y: auto !important;
            padding: 12px 14px 120px 14px !important;
            -webkit-overflow-scrolling: touch !important;
            box-sizing: border-box !important;
        }

        .comanda-content {
            display: flex !important;
            flex-direction: column !important;
            gap: 16px !important;
        }

        .comanda-items-col,
        .comanda-summary-col {
            min-width: 0 !important;
            width: 100% !important;
            overflow: visible !important;
        }

        .btn-add-item {
            width: 100% !important;
            min-height: 44px !important;
            height: 44px !important;
            justify-content: center !important;
            font-size: 13.5px !important;
            font-weight: 700 !important;
            border-radius: 10px !important;
            margin-top: 4px !important;
            touch-action: manipulation;
        }

        .quick-product-picker {
            background: #FFFDF9 !important;
            border: 1.5px solid #FF6A00 !important;
            border-radius: 12px !important;
            padding: 12px !important;
            box-shadow: 0 4px 16px rgba(255, 106, 0, 0.08) !important;
            margin-bottom: 12px !important;
        }

        .picker-search {
            min-height: 44px !important;
            font-size: 14px !important;
            border-radius: 8px !important;
            padding: 0 12px !important;
            box-sizing: border-box !important;
        }

        .picker-list {
            max-height: 240px !important;
            gap: 8px !important;
            -webkit-overflow-scrolling: touch !important;
        }

        .picker-item {
            min-height: 48px !important;
            padding: 8px 12px !important;
            border-radius: 8px !important;
            touch-action: manipulation;
        }

        .picker-add-btn {
            min-height: 38px !important;
            padding: 6px 12px !important;
            font-size: 12px !important;
            font-weight: 700 !important;
            border-radius: 6px !important;
        }

        .stepper-mini {
            padding: 2px 4px !important;
            gap: 4px !important;
            display: inline-flex !important;
            align-items: center !important;
        }

        .btn-step-mini {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            min-height: 44px !important;
            font-size: 20px !important;
            font-weight: 800 !important;
            border-radius: 8px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: #F1F5F9 !important;
            color: #0F172A !important;
            touch-action: manipulation;
        }

        .step-mini-val {
            font-size: 14px !important;
            font-weight: 800 !important;
            min-width: 26px !important;
            text-align: center !important;
            color: #0F172A !important;
        }

        .btn-item-delete {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            min-height: 44px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 0 !important;
            border-radius: 8px !important;
            background: #FEE2E2 !important;
            color: #EF4444 !important;
            touch-action: manipulation;
        }

        .btn-item-edit {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            min-height: 44px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 0 !important;
            border-radius: 8px !important;
            background: #F1F5F9 !important;
            color: #475569 !important;
            touch-action: manipulation;
        }

        .batch-pending-box {
            border: 1.5px solid #FCD34D !important;
            border-radius: 12px !important;
            background: #FFFBEB !important;
            padding: 12px 12px 8px 12px !important;
            margin-bottom: 12px !important;
        }

        .batch-footer-controls {
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            background: #FFFFFF !important;
            border-top: 1.5px solid #FED7AA !important;
            box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.14) !important;
            padding: 8px 14px calc(10px + env(safe-area-inset-bottom, 0px)) 14px !important;
            z-index: 10005 !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 6px !important;
        }

        .batch-dest-bar {
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 8px !important;
        }

        .batch-dest-label {
            font-size: 10.5px !important;
            font-weight: 700 !important;
            color: #78350F !important;
        }

        .batch-dest-pills {
            display: flex !important;
            gap: 4px !important;
        }

        .dest-pill {
            padding: 4px 8px !important;
            font-size: 11px !important;
            font-weight: 700 !important;
            border-radius: 6px !important;
        }

        .batch-actions-bar {
            display: flex !important;
            gap: 8px !important;
            width: 100% !important;
        }

        .btn-batch-send {
            flex: 1 !important;
            max-width: 100% !important;
            min-height: 48px !important;
            height: 48px !important;
            font-size: 13.5px !important;
            font-weight: 800 !important;
            border-radius: 10px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 6px !important;
            box-shadow: 0 4px 14px rgba(234, 88, 12, 0.35) !important;
            touch-action: manipulation;
        }

        .btn-batch-save-only {
            min-height: 48px !important;
            height: 48px !important;
            padding: 0 12px !important;
            font-size: 11px !important;
            border-radius: 10px !important;
            white-space: nowrap !important;
        }

        .comanda-items-list {
            max-height: none !important;
            border: none !important;
            background: transparent !important;
            overflow-x: hidden !important;
        }

        .comanda-table {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            border-collapse: separate !important;
            gap: 10px !important;
        }

        .comanda-table thead {
            display: none !important;
        }

        .comanda-table tbody {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            gap: 10px !important;
        }

        .comanda-table tbody tr {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: wrap !important;
            align-items: center !important;
            justify-content: space-between !important;
            width: 100% !important;
            background: #FFFFFF !important;
            border: 1.5px solid #E2E8F0 !important;
            border-radius: 12px !important;
            padding: 12px 14px !important;
            box-shadow: 0 1px 4px rgba(15, 23, 42, 0.05) !important;
            gap: 8px 10px !important;
            box-sizing: border-box !important;
        }

        .comanda-table td {
            padding: 0 !important;
            border: none !important;
        }

        .comanda-table td.name-cell {
            order: 1 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            padding-bottom: 8px !important;
            border-bottom: 1px dashed #E2E8F0 !important;
        }

        .name-cell-header {
            display: flex !important;
            justify-content: space-between !important;
            align-items: baseline !important;
            gap: 8px !important;
        }

        .comanda-table td.name-cell strong {
            font-size: 15px !important;
            font-weight: 800 !important;
            color: #0F172A !important;
            line-height: 1.3 !important;
            word-break: break-word !important;
            white-space: normal !important;
        }

        .mobile-item-total {
            display: inline-block !important;
            font-size: 15.5px !important;
            font-weight: 800 !important;
            color: #0F172A !important;
            white-space: nowrap !important;
        }

        .comanda-table .item-notes {
            font-size: 12px !important;
            color: #B45309 !important;
            background: #FEF3C7 !important;
            padding: 4px 8px !important;
            border-radius: 6px !important;
            border: 1px solid #FDE68A !important;
            display: block !important;
            margin-top: 4px !important;
            word-break: break-word !important;
            white-space: normal !important;
        }

        .comanda-table td.price-cell,
        .comanda-table td.total-cell {
            display: none !important;
        }

        .comanda-table td.qty-cell {
            order: 2 !important;
            flex: 1 1 auto !important;
            width: auto !important;
            min-width: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            gap: 8px !important;
            padding: 0 !important;
            border: none !important;
        }

        .unit-price-mobile {
            display: inline-block !important;
            font-size: 12.5px !important;
            color: #64748B !important;
            font-weight: 700 !important;
            white-space: nowrap !important;
        }

        .comanda-table td.actions-cell {
            order: 3 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            display: flex !important;
            justify-content: flex-end !important;
            align-items: center !important;
            padding: 0 !important;
            border: none !important;
            margin-top: 0 !important;
            pointer-events: auto !important;
        }

        .comanda-table td.actions-cell > div {
            display: flex !important;
            align-items: center !important;
            gap: 6px !important;
            flex-wrap: wrap !important;
            justify-content: flex-end !important;
        }

        .comanda-table .btn-item-edit,
        .comanda-table .btn-item-delete {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            min-height: 44px !important;
            border-radius: 8px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            touch-action: manipulation !important;
        }

        .comanda-table .btn-item-rectify {
            min-height: 44px !important;
            height: 44px !important;
            padding: 0 10px !important;
            border-radius: 8px !important;
            font-size: 12px !important;
            font-weight: 700 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 5px !important;
            touch-action: manipulation !important;
        }
    }
</style>
